<script setup lang="ts">
import { useOnboarding } from '@/composables/useOnboarding';
import { useUserStore } from '@/stores/useUserStore';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const userStore = useUserStore();
const router = useRouter();
const {
  resetOnboarding: resetOnboardingAPI,
  navigateToChapter,
  getCurrentChapter,
  startTour,
  isChapterAvailableForRole,
  getDisplayableChaptersForRole,
} = useOnboarding();

const completedChapters = ref<string[]>([]);
const currentChapter = ref<string | null>(null);
const showContinueDialog = ref(false);

const isFullyCompleted = computed(() => {
  return completedChaptersCount.value === totalChapters.value && totalChapters.value > 0;
});

// Tous les chapitres possibles
const allChapters = [
  {
    key: '01_Introduction',
    title: t('onboarding.chapters.introduction.title'),
    description: t('onboarding.chapters.introduction.description'),
  },
  {
    key: '02_RecipeGeneration',
    title: t('onboarding.chapters.recipeGeneration.title'),
    description: t('onboarding.chapters.recipeGeneration.description'),
  },
  {
    key: '03_RecipeDetails',
    title: t('onboarding.chapters.recipeDetails.title'),
    description: t('onboarding.chapters.recipeDetails.description'),
  },
  {
    key: '04_RecipeActions',
    title: t('onboarding.chapters.recipeActions.title'),
    description: t('onboarding.chapters.recipeActions.description'),
  },
  {
    key: '05_Organization',
    title: t('onboarding.chapters.organization.title'),
    description: t('onboarding.chapters.organization.description'),
  },
  {
    key: '06_Scan',
    title: t('onboarding.chapters.scan.title'),
    description: t('onboarding.chapters.scan.description'),
  },
  {
    key: '07_ProfileAndTeam',
    title: t('onboarding.chapters.profileAndTeam.title'),
    description: t('onboarding.chapters.profileAndTeam.description'),
  },
  {
    key: '08_Planning',
    title: t('onboarding.chapters.planning.title'),
    description: t('onboarding.chapters.planning.description'),
  },
];

// Filtrer les chapitres selon le rôle de l'utilisateur (exclut le chapitre de fin)
const chapters = computed(() => {
  const displayableKeys = getDisplayableChaptersForRole(userStore.contextualRole || 'admin');
  return allChapters.filter((chapter) => displayableKeys.includes(chapter.key));
});

const totalChapters = computed(() => chapters.value.length); // Chapitres disponibles pour le rôle
const completedChaptersCount = computed(() => {
  const progress = userStore.user?.onboarding_progress || {};
  return chapters.value.filter((chapter) => progress[chapter.key]?.completed === true).length;
});
const progressPercentage = computed(() => {
  if (totalChapters.value === 0) return 0;
  return Math.round((completedChaptersCount.value / totalChapters.value) * 100);
});

const statusBadgeClass = computed(() => {
  const userMarkedComplete = userStore.user?.onboarding_complete === true;

  if (userMarkedComplete) return 'bg-green-50 text-green-700 border border-green-200';

  const completedCount = completedChaptersCount.value;
  const totalCount = totalChapters.value;

  if (completedCount >= Math.ceil(totalCount * 0.75)) return 'bg-blue-50 text-blue-700 border border-blue-200';
  if (completedCount > 0) return 'bg-accent-light text-primary-800 border border-accent';
  return 'bg-surface-100 text-surface-600 border border-surface-300';
});

const statusIcon = computed(() => {
  const userMarkedComplete = userStore.user?.onboarding_complete === true;

  if (userMarkedComplete) return 'pi pi-check-circle';

  const progress = userStore.user?.onboarding_progress || {};
  const completedCount = chapters.value.filter((chapter) => progress[chapter.key]?.completed === true).length;
  const totalCount = chapters.value.length;

  if (completedCount >= Math.ceil(totalCount * 0.75)) return 'pi pi-verified';
  if (completedCount > 0) return 'pi pi-clock';
  return 'pi pi-circle';
});

const statusText = computed(() => {
  const progress = userStore.user?.onboarding_progress || {};
  const completedCount = chapters.value.filter((chapter) => progress[chapter.key]?.completed === true).length;
  const totalCount = chapters.value.length;

  if (completedCount === totalCount) return t('profile.onboarding.status.completed');
  if (completedCount >= Math.ceil(totalCount * 0.75)) return t('profile.onboarding.status.mostly_completed');
  if (completedCount > 0) return t('profile.onboarding.status.inProgress');
  return t('profile.onboarding.status.notStarted');
});

function getChapterCardClass(chapterKey: string): string {
  if (isCompleted(chapterKey)) return 'border-green-200 bg-green-50/30 hover:border-green-300';
  if (isCurrent(chapterKey)) return 'border-primary-300 bg-primary-50/30 hover:border-primary-400';
  return 'border-surface-200 hover:border-primary-300';
}

function getChapterNumberClass(chapterKey: string): string {
  if (isCompleted(chapterKey)) return 'bg-green-500 text-white';
  if (isCurrent(chapterKey)) return 'bg-primary-600 text-white';
  return 'bg-surface-200 text-surface-600';
}

function getChapterIconClass(chapterKey: string): string {
  if (isCompleted(chapterKey)) return 'text-green-500';
  if (isCurrent(chapterKey)) return 'text-primary-600';
  return 'text-surface-400';
}

onMounted(() => {
  loadOnboardingData();
});

function loadOnboardingData() {
  loadProgressFromUser();
  currentChapter.value = getCurrentChapter();
}

function loadProgressFromUser() {
  const progress = userStore.user?.onboarding_progress || {};
  completedChapters.value = [];

  // Parcourir tous les chapitres et voir lesquels sont complétés
  Object.entries(progress).forEach(([chapterKey, chapterData]) => {
    if (chapterData?.completed) {
      completedChapters.value.push(chapterKey);
    }
  });

  console.log("[OnboardingOverview] Progrès chargé depuis l'API:", {
    completedChapters: completedChapters.value,
    userProgress: progress,
  });
}

function isCompleted(chapterKey: string): boolean {
  const progress = userStore.user?.onboarding_progress || {};
  return progress[chapterKey]?.completed === true;
}

function isCurrent(chapterKey: string): boolean {
  return currentChapter.value === chapterKey;
}

function isAvailable(chapterKey: string): boolean {
  // Vérifier si le chapitre est disponible pour le rôle actuel
  return isChapterAvailableForRole(chapterKey);
}

function getChapterNumber(chapterKey: string): string {
  // Extraire le numéro du chapitre de la clé (ex: "01_Introduction" -> "1")
  const match = chapterKey.match(/^(\d+)_/);
  return match ? parseInt(match[1], 10).toString() : '';
}

function getCompletionDate(chapterKey: string): string {
  const progress = userStore.user?.onboarding_progress || {};
  const chapterData = progress[chapterKey];

  if (chapterData && chapterData.completed_at) {
    return new Date(chapterData.completed_at).toLocaleDateString();
  }

  return new Date().toLocaleDateString(); // Fallback
}

async function goToChapter(chapterKey: string) {
  if (!isAvailable(chapterKey)) return;

  try {
    console.log(`[OnboardingOverview] Navigation vers le chapitre: ${chapterKey}`);

    // Nettoyer les overlays existants
    document.querySelectorAll('.p-component-overlay').forEach((el) => el.remove());

    // Utiliser navigateToChapter qui gère la navigation et le démarrage du tour
    await navigateToChapter(chapterKey);

    console.log(`[OnboardingOverview] Navigation terminée vers: ${chapterKey}`);
  } catch (error) {
    console.error('Erreur lors de la navigation vers le chapitre:', error);
  }
}

const resetOnboarding = async () => {
  await resetOnboardingAPI();
  await router.push('/');
  startTour();
};

const getNextIncompleteChapter = () => {
  return chapters.value.find((chapter) => !isCompleted(chapter.key));
};

const handleStatusClick = () => {
  showContinueDialog.value = true;
};

const continueTour = async () => {
  showContinueDialog.value = false;

  if (completedChaptersCount.value === 0) {
    // Start from beginning
    await resetOnboardingAPI();
    await router.push('/');
    startTour();
  } else if (isFullyCompleted.value) {
    // Restart onboarding
    await resetOnboarding();
  } else {
    // Continue to next incomplete chapter
    const nextChapter = getNextIncompleteChapter();
    if (nextChapter) {
      await goToChapter(nextChapter.key);
    }
  }
};
</script>
<template>
  <div class="onboarding-status-page min-h-screen">
    <div class="container mx-auto px-4 py-8">
      <div class="header-section mb-12 text-center">
        <p class="mx-auto max-w-2xl text-lg text-primary-700">{{ t('profile.onboarding.description') }}</p>
      </div>

      <div class="status-overview mb-12">
        <div class="progress-card rounded-xl border border-surface-200 bg-surface-0 p-8 shadow-clickable-card">
          <div class="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div class="progress-info w-full flex-1 items-center">
              <h2 class="mb-6 font-bold font-titles text-2xl text-primary-950">{{ t('profile.onboarding.progressTitle') }}</h2>
              <div class="progress-bar mb-3 h-3 w-full overflow-hidden rounded-full bg-surface-200">
                <div
                  class="progress-fill h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-700 transition-all duration-500 ease-out"
                  :style="{ width: `${progressPercentage}%` }"
                ></div>
              </div>
              <p class="text-sm text-primary-600">
                {{ progressPercentage }}% {{ t('profile.onboarding.chaptersCompleted') }} ({{ completedChaptersCount }} / {{ totalChapters }})
              </p>
            </div>
            <div
              class="status-badge flex cursor-pointer items-center gap-3 rounded-lg px-6 py-3 font-semibold transition-opacity hover:opacity-80"
              :class="statusBadgeClass"
              @click="handleStatusClick"
            >
              <i :class="statusIcon" class="text-lg"></i>
              <span>{{ statusText }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="actions-section mb-12">
        <div class="flex justify-center">
          <Button :label="t('profile.onboarding.restartOnboarding')" icon="pi pi-refresh" class="px-8 py-3 font-semibold" @click="resetOnboarding" />
        </div>
      </div>

      <div class="chapters-section">
        <h2 class="mb-8 font-bold font-titles text-3xl text-primary-950">{{ t('profile.onboarding.chaptersTitle') }}</h2>
        <div class="chapters-grid grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          <div
            v-for="chapter in chapters"
            :key="chapter.key"
            class="chapter-card cursor-pointer rounded-xl border-2 bg-surface-0 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-clickable-card"
            :class="getChapterCardClass(chapter.key)"
            @click="goToChapter(chapter.key)"
          >
            <div class="chapter-header mb-4 flex items-center justify-between">
              <div
                class="chapter-number flex h-12 w-12 items-center justify-center rounded-full font-bold text-lg transition-colors duration-300"
                :class="getChapterNumberClass(chapter.key)"
              >
                {{ getChapterNumber(chapter.key) }}
              </div>
              <div class="chapter-status-icon text-xl transition-colors duration-300" :class="getChapterIconClass(chapter.key)">
                <i v-if="isCompleted(chapter.key)" class="pi pi-check"></i>
                <i v-else-if="isCurrent(chapter.key)" class="pi pi-play"></i>
                <i v-else class="pi pi-circle"></i>
              </div>
            </div>
            <div class="chapter-content">
              <h3 class="mb-2 font-bold font-titles text-xl text-primary-950">{{ chapter.title }}</h3>
              <p class="mb-4 text-sm leading-relaxed text-primary-600">{{ chapter.description }}</p>
              <div class="chapter-meta">
                <span v-if="isCompleted(chapter.key)" class="rounded-full bg-green-50 px-3 py-1 font-semibold text-xs text-green-600">
                  {{ t('profile.onboarding.completedOn') }}: {{ getCompletionDate(chapter.key) }}
                </span>
                <span v-else-if="isCurrent(chapter.key)" class="rounded-full bg-primary-50 px-3 py-1 font-semibold text-xs text-primary-700">
                  {{ t('profile.onboarding.currentChapter') }}
                </span>
                <span v-else class="rounded-full bg-surface-100 px-3 py-1 font-semibold text-xs text-surface-500">
                  {{ t('profile.onboarding.notStarted') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Continue Dialog -->
  <Dialog v-model:visible="showContinueDialog" modal :draggable="false" :closable="true" class="w-11/12 md:w-1/2 lg:w-1/3">
    <template #header>
      <div class="flex items-center gap-3">
        <i
          :class="
            isFullyCompleted
              ? 'pi pi-check-circle text-green-600'
              : completedChaptersCount === 0
                ? 'pi pi-play-circle text-primary-600'
                : 'pi pi-info-circle text-primary-600'
          "
          class="text-2xl"
        ></i>
        <h3 class="font-bold font-titles text-xl text-primary-950">
          {{
            isFullyCompleted
              ? t('profile.onboarding.continueDialog.completedTitle')
              : completedChaptersCount === 0
                ? t('profile.onboarding.continueDialog.startTitle')
                : t('profile.onboarding.continueDialog.title')
          }}
        </h3>
      </div>
    </template>

    <div class="py-4">
      <!-- All completed -->
      <div v-if="isFullyCompleted">
        <p class="mb-4 text-primary-700">{{ t('profile.onboarding.continueDialog.completedMessage') }}</p>
        <div class="flex items-center gap-3 rounded-lg border-2 border-green-200 bg-green-50/30 p-4">
          <i class="pi pi-check-circle text-3xl text-green-600"></i>
          <div>
            <h4 class="font-bold text-green-700">{{ t('profile.onboarding.continueDialog.allDone') }}</h4>
            <p class="text-sm text-green-600">{{ progressPercentage }}% {{ t('profile.onboarding.chaptersCompleted') }}</p>
          </div>
        </div>
      </div>

      <!-- Not started -->
      <div v-else-if="completedChaptersCount === 0">
        <p class="mb-4 text-primary-700">{{ t('profile.onboarding.continueDialog.startMessage') }}</p>
        <div v-if="chapters.length > 0" class="rounded-lg border-2 border-primary-200 bg-primary-50/30 p-4">
          <div class="mb-2 flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 font-bold text-white">
              {{ getChapterNumber(chapters[0].key) }}
            </div>
            <h4 class="font-bold font-titles text-lg text-primary-950">{{ chapters[0].title }}</h4>
          </div>
          <p class="ml-13 text-sm text-primary-600">{{ chapters[0].description }}</p>
        </div>
      </div>

      <!-- In progress -->
      <div v-else>
        <p class="mb-4 text-primary-700">{{ t('profile.onboarding.continueDialog.message') }}</p>

        <div v-if="getNextIncompleteChapter()" class="rounded-lg border-2 border-primary-200 bg-primary-50/30 p-4">
          <div class="mb-2 flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 font-bold text-white">
              {{ getChapterNumber(getNextIncompleteChapter()!.key) }}
            </div>
            <h4 class="font-bold font-titles text-lg text-primary-950">{{ getNextIncompleteChapter()!.title }}</h4>
          </div>
          <p class="ml-13 text-sm text-primary-600">{{ getNextIncompleteChapter()!.description }}</p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <Button :label="t('common.cancel')" severity="secondary" @click="showContinueDialog = false" />
        <Button
          :label="
            isFullyCompleted
              ? t('profile.onboarding.continueDialog.restart')
              : completedChaptersCount === 0
                ? t('profile.onboarding.continueDialog.start')
                : t('profile.onboarding.continueDialog.continue')
          "
          :icon="isFullyCompleted ? 'pi pi-refresh' : 'pi pi-play'"
          @click="continueTour"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
/* Styles spécifiques pour les animations */
.progress-fill {
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Style pour le survol des cartes de chapitre */
.chapter-card:hover {
  transform: translateY(-4px);
}

/* Animation pour les badges de statut */
.status-badge {
  transition: all 0.3s ease;
}
</style>
